/**
 * Navigation History Plugin
 *
 * Tracks user navigation history in session storage to provide a reliable way
 * to determine if the user can actually navigate back. This persists across
 * page refreshes unlike window.history.length which includes forward history.
 *
 * Fallback: If session storage is unavailable, uses in-memory tracking. This won't persist across refreshes
 * but still works better than window.history.length for SPA navigation.
 */

const STORAGE_KEY = 'oc_navigation_history'
const MAX_HISTORY_LENGTH = 50

// In-memory fallback for when session storage is unavailable
let inMemoryHistory = []

/**
 * Get the navigation history from session storage
 * @returns {Array} Array of navigation history entries
 */
function getNavigationHistory() {
    if (typeof window === 'undefined') {
        return []
    }

    if (window.sessionStorage) {
        try {
            const historyStr = window.sessionStorage.getItem(STORAGE_KEY)
            return historyStr ? JSON.parse(historyStr) : []
        } catch {
            // Fall through to in-memory fallback
        }
    }

    return inMemoryHistory
}

/**
 * Save navigation history to session storage
 * @param {Array} history - Navigation history array
 */
function saveNavigationHistory(history) {
    if (typeof window === 'undefined') {
        return
    }

    if (window.sessionStorage) {
        try {
            window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(history))
            return
        } catch {
            // Fall through to in-memory fallback
        }
    }

    inMemoryHistory = history
}

/**
 * Add a new entry to navigation history
 * @param {object} route - Vue Router route object
 */
function addToNavigationHistory(route) {
    if (!route || !route.fullPath) {
        return
    }

    const history = getNavigationHistory()
    const entry = {
        fullPath: route.fullPath,
        name: route.name,
        params: route.params,
        timestamp: Date.now(),
    }

    history.push(entry)

    // Keep history at a reasonable size
    if (history.length > MAX_HISTORY_LENGTH) {
        history.shift()
    }

    saveNavigationHistory(history)
}

/**
 * Check if the user can navigate back
 * @returns {boolean} True if user has navigation history to go back to
 */
function canNavigateBack() {
    const history = getNavigationHistory()

    // User can go back if there's more than 1 entry
    // (current page + at least one previous page)
    return history.length > 1
}

/**
 * Clear navigation history
 * Useful for testing or when user logs out
 */
function clearNavigationHistory() {
    if (typeof window === 'undefined') {
        return
    }

    if (window.sessionStorage) {
        try {
            window.sessionStorage.removeItem(STORAGE_KEY)
        } catch {
            // Ignore errors
        }
    }

    inMemoryHistory = []
}

/**
 * Initialize navigation history
 * Should be called on app initialization
 *
 * IMPORTANT: We clear history on every page load/refresh because:
 * - Browser back/forward doesn't trigger Vue Router hooks
 * - After using browser back then refreshing, session storage would be stale
 * - Starting fresh on each page load ensures accuracy
 */
function initNavigationHistory() {
    if (typeof window === 'undefined') {
        return
    }

    // Always start fresh on page load/refresh to avoid stale history
    // from browser back/forward navigation
    // This works with both session storage and in-memory fallback
    clearNavigationHistory()
}

export default ({ app }, inject) => {
    inject('navigationHistory', {
        canNavigateBack,
    })

    initNavigationHistory()

    app.router.afterEach((to) => {
        addToNavigationHistory(to)
    })
}
